import React from 'react';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  return (
    <div className="pt-16">
      <div className="bg-white text-[#2b603e] py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#2b603e] to-[#1a3d25]">About Us</h1>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <hr className="border-t border-gray-200 my-0" />
      </div>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <img 
                src={require('../assets/images/logowithoutlogo.png')} 
                alt="Asclepius Logo" 
                className="w-full max-w-md mx-auto transform transition-transform duration-300 hover:scale-105" 
              />
            </div>
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2b603e] to-[#1a3d25] mb-4">
                Our Story
              </h2>
              <p className="text-gray-600 mb-4">
                At Asclepius, we believe in honoring the sacred balance of body, mind, and spirit through nature's time-tested gifts. Our journey began with a simple but powerful vision: to reconnect modern life with ancient wisdom and rituals that have nourished humanity for centuries.
              </p>
              <p className="text-gray-600 mb-4">
                Partnering closely with Himalayan communities, we source the purest Shilajit and sacred herbs harvested with respect for tradition, the environment, and the people who have safeguarded this knowledge across generations. Every batch we offer reflects this commitment to authenticity, quality, and reverence.
              </p>
              <p className="text-gray-600 mb-4">
                Our products are not quick fixes or fleeting trends. They are natural allies on your path to intentional living tools that support your connection to yourself and the world around you.
              </p>
              <p className="text-gray-600">
                Choosing Asclepius means more than buying a product. It's a conscious step toward living with purpose, clarity, and harmony honoring your body as a temple and embracing a lifestyle rooted in balance and respect.
              </p>
              <p className="text-gray-600 mt-4">
                Join us on this journey back to source.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#2b603e] to-[#1a3d25] mb-2">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              At Asclepius, our values guide everything we do, from sourcing our ingredients to serving our customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Purity</h3>
              <p className="text-gray-600">
                We are committed to providing the purest, most potent natural supplements. Our rigorous quality control ensures that every product meets the highest standards of purity and effectiveness.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.56-.5-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.56.5.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.498-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Sustainability</h3>
              <p className="text-gray-600">
                We believe in sustainable sourcing practices that respect the environment and support local communities. Our commitment to sustainability extends to every aspect of our business.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Transparency</h3>
              <p className="text-gray-600">
                We are committed to providing clear and honest information about our products, including their origins and production processes. We share detailed insights on how our ingredients are sourced, processed, and tested to ensure consistent quality and support for your overall wellness journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#2b603e] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Wellness Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Discover the ancient power of Himalayan Shilajit and unlock your body's natural potential.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link 
              to="/products" 
              className="inline-block bg-white text-[#2b603e] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition-colors text-lg"
            >
              Explore Products
            </Link>
            <Link 
              to="/contact" 
              className="inline-block bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#2b603e] font-bold py-3 px-8 rounded-full transition-colors text-lg"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;